import { useEffect, useRef } from "react"
import * as PropTypes from "prop-types"
import isEmpty from "lodash/isEmpty"
import join from "lodash/join"
import styled from "styled-components"
import { numberWithDots } from "helpers/formatter"
import Typography from "components/styled/Typography"
import Icon from "components/icons/icon"
import { Info } from "components/icons/info"
import { scrollToRef } from "helpers/scroll"

// eslint-disable-next-line react/jsx-props-no-spreading
const Field = styled(({ hasData, color, transparent, ...rest }) => <input {...rest} />)`
  width: 100%;
  ${({ theme, color, transparent, disabled }) =>
    disabled
      ? `
      color: ${theme.colors.gray};
      background: ${theme.colors.lightGray};
    `
      : `
      color: ${theme.colors[color]};
      background: ${transparent ? "transparent" : "white"};
    `}
  font-size: 21px;
  line-height: 40px;
  font-weight: 500;
  height: 40px;
  border: none;

  ::placeholder {
    font-family: "Rubik", sans-serif;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.silver};
    font-weight: 400;
  }

  :focus {
    box-shadow: none;
    border-image: initial;
    outline: 0;
  }

  .is-invalid & {
    color: ${({ theme }) => theme.colors.crimson};
  }
`

const InputContainer = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: center;
  padding: 10px 15px;
  border-radius: 6px;
  height: 60px;
  ${({ theme, color, hasData, activeBorder, transparent, disabled }) =>
    disabled
      ? `
      border: 1px solid ${theme.colors.gray};
      background: ${theme.colors.lightGray};
    `
      : `
      border: 1px solid ${theme.colors[activeBorder || hasData ? color : "silver"]};
      background: ${transparent ? "transparent" : "white"};
    `}

  &:focus-within {
    border: 1px solid ${({ theme, color }) => theme.colors[color]};
  }

  input::-webkit-calendar-picker-indicator {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0;
  }

  &.is-invalid {
    border-color: ${({ theme }) => theme.colors.crimson};
  }
`

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Feedback = styled(Typography)`
  && {
    font-size: 1rem;
    width: 100%;
    margin: 0.25rem 0 0;
  }
`

const InfoContent = styled.div`
  display: none;
  background: ${({ theme }) => theme.colors.darkBlue};
  padding: 15px;
  border-radius: 6px;
  box-shadow: 0 6px 18px 0 rgba(2, 24, 89, 0.6);
  position: absolute;
  left: 0;
  width: 100%;
  top: 65px;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 15px;
    height: 25px;
    width: 25px;
    background: ${({ theme }) => theme.colors.darkBlue};
    transform: rotate(-45deg);
  }
`

const InfoWrapper = styled.div`
  display: flex;

  &:hover {
    ${InfoContent} {
      display: block;
    }
  }
`

const InfoIcon = styled(Icon)`
  margin-left: 15px;
`

export default function BasicInput(props) {
  const {
    name,
    errors,
    formName,
    value,
    handleChange,
    helpText,
    color,
    activeBorder,
    label,
    Leading,
    Trailing,
    ExtraInfo,
    transparent,
    disabled,
    number,
    noScroll,
    ...rest
  } = props
  const ref = useRef(null)

  const errorsValue = errors && errors[name]

  const errorsText = Array.isArray(errorsValue) ? join(errorsValue) : errorsValue

  const onChange = e => {
    const { target } = e
    let newVal = target.type === "checkbox" ? target.checked : target.value
    if (number) {
      newVal = newVal.replace(/\./g, "")
      if (Number.isNaN(Number(newVal))) return
    }
    if (handleChange) handleChange(name, newVal)
  }

  useEffect(() => {
    if (isEmpty(errors) || noScroll) return
    const firstError = Object.keys(errors)[0]
    if (firstError === name) scrollToRef(ref, 100, true)
  }, [errors])

  return (
    <div ref={ref} style={{ marginBottom: "15px" }}>
      <InputContainer
        activeBorder={activeBorder}
        className={!isEmpty(errorsValue) ? "is-invalid" : ""}
        color={color}
        disabled={disabled}
        hasData={Boolean(value)}
        transparent={transparent}>
        {label && (
          <Typography variant="small" weight="medium">
            {label}
          </Typography>
        )}
        <InputWrapper>
          {Leading && <Leading />}
          <Field
            color={color}
            disabled={disabled}
            id={`${formName}_${name}`}
            name={name}
            transparent={transparent}
            value={number ? numberWithDots(value) : value || ""}
            onChange={onChange}
            {...rest}
          />
          {Trailing && <Trailing />}
          {ExtraInfo && (
            <InfoWrapper>
              <InfoIcon Icon={Info} color="deepPink" size="sm" />
              <InfoContent>
                <ExtraInfo />
              </InfoContent>
            </InfoWrapper>
          )}
        </InputWrapper>
      </InputContainer>
      {errorsText && (
        <Feedback color="crimson" style={{ textAlign: "left" }} weight="medium">
          {errorsText}
        </Feedback>
      )}
      {helpText && !errorsText && (
        <Feedback color="silver" weight="medium">
          {helpText}
        </Feedback>
      )}
    </div>
  )
}

BasicInput.defaultProps = {
  type: "text",
  color: "darkBlue",
  activeBorder: false,
  value: "",
  transparent: true,
  number: false,
  disabled: false,
  helpText: null,
  label: null,
  Leading: null,
  Trailing: null,
  ExtraInfo: null,
  noScroll: false
}

BasicInput.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  errors: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleChange: PropTypes.func.isRequired,
  helpText: PropTypes.string,
  color: PropTypes.string,
  activeBorder: PropTypes.bool,
  label: PropTypes.string,
  Leading: PropTypes.func,
  Trailing: PropTypes.func,
  ExtraInfo: PropTypes.func,
  transparent: PropTypes.bool,
  disabled: PropTypes.bool,
  number: PropTypes.bool,
  noScroll: PropTypes.bool
}
